/**
 * S11, the table grid. Browse and edit rows the way a spreadsheet works,
 * without ever letting somebody lose data by accident.
 *
 * THE SAFETY RAIL IS THE DESIGN. Nothing a person types is written when they
 * leave the cell. Edits stage, the cells they touched carry a marker, a bar
 * says how many are waiting, and the way through it is a sheet that shows
 * every old value beside every new one before anything is sent. That is the
 * only reason this screen can be handed to a first time builder at all, and
 * it is why editing rows is a Simple feature rather than an Advanced one: a
 * person can absolutely need to fix a typo in a row, and doing it wrong here
 * is recoverable because they see it before it happens.
 *
 * THE ROW CAP IS A NORMAL CONDITION, NOT A FAULT. cloud-api hands back one
 * page of rows at a time, so the footer says so, in the same muted voice as
 * the count beside it. No amber, no glyph, no exclamation mark. Nothing has
 * gone wrong: a person asked for a table and got a fast page of it. When the
 * table can be paged (a single-column primary key), "Show more rows" adds the
 * next page below, in key order, rather than replacing the one on screen, so
 * staged edits are never thrown away by turning a page.
 *
 * THE KEYBOARD IS REAL, not described. Arrows move, Home and End jump, Tab
 * moves right and wraps, Enter edits, Escape cancels the cell, Space picks
 * the row, and Command or Control with Enter sends everything pending. That
 * map lives in the grid component, so the query runner's result grid inherits
 * every bit of it.
 *
 * WHAT ADVANCED ADDS, and it only ever adds: column types in the header, and
 * below the grid, in one disclosure, the zebra toggle, the schema, the
 * qualified table name and the query runner. That block used to be a raw mode
 * check with no chip and no trigger, so it was the only Advanced block in the
 * product that was both unmarked and unreachable from Simple. It is a
 * disclosure now: one click away in Simple, already open and chipped in
 * Advanced, and the same content either way. The grid, the editing and the
 * pending bar are identical in both modes.
 *
 * STATES, all deep linkable: ?state=edits, ?state=diff, ?state=no-rows,
 * ?state=delete, ?state=filter, ?state=loading.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Checkbox } from '../../components/primitives/Checkbox';
import { Field } from '../../components/primitives/Field';
import { Select } from '../../components/primitives/Select';
import { Table } from '../../components/primitives/Table';
import { showToast } from '../../components/primitives/Toast';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { CopyField } from '../../components/patterns/CopyField';
import { DataGrid } from '../../components/patterns/DataGrid';
import type { Edits, GridSort } from '../../components/patterns/DataGrid';
import { DestructiveConfirm } from '../../components/patterns/DestructiveConfirm';
import { DiffSheet } from '../../components/patterns/DiffSheet';
import type { CellChange, RowAddition } from '../../components/patterns/DiffSheet';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';

import { deleteRow, getProject, insertRow, listRowsPage, listTables, updateRow } from '../../lib/api';
import { ApiError } from '../../lib/http';
import { count } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { t } from '../../lib/i18n/t';
import { STAGED_EDITS } from '../../lib/mock/seed';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { DbCell, DbColumn, DbRow, DbTable } from '../../lib/types';

import { isSystemTable } from './Database';
import { QueryPanel } from './QueryRunner';

import '../../styles/screens/database.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/** How long a save narrates before the rows change under it. Speed scaled. */
export const SAVE_MS = 500;

/** Above this many rows, deleting asks a person to type the count. */
export const TYPE_TO_CONFIRM_ABOVE = 10;

type FilterOp = 'is' | 'contains' | 'lt' | 'gt';

interface Filter {
  column: string;
  op: FilterOp;
  value: string;
}

/* ------------------------------------------------------------- utilities */

/** Editing is desktop first. On a phone this screen reads and does not write. */
function useSmallScreen(): boolean {
  const [small, setSmall] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(max-width: 760px)');
    const onChange = () => setSmall(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return small;
}

/**
 * The column a person recognizes a row by: the first piece of text in it that
 * is not an identifier. Every confirm and every diff line is written against
 * this, because "Delete row 4" is a sentence about the database and "Delete
 * Green mango salad" is a sentence about the thing they made.
 */
export function humanColumn(columns: DbColumn[]): DbColumn {
  return columns.find((one) => one.type === 'text' && !one.primaryKey) ?? columns[0];
}

function cellText(value: DbCell): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function matches(row: DbRow, filter: Filter): boolean {
  const cell = row.cells[filter.column];
  const wanted = filter.value.trim();
  if (wanted === '') return true;

  if (filter.op === 'lt' || filter.op === 'gt') {
    const left = Number(cell);
    const right = Number(wanted);
    if (Number.isNaN(left) || Number.isNaN(right)) return false;
    return filter.op === 'lt' ? left < right : left > right;
  }

  const text = cellText(cell).toLowerCase();
  const needle = wanted.toLowerCase();
  return filter.op === 'is' ? text === needle : text.includes(needle);
}

/** cloud-api's own reasons a table's rows can't be individually addressed for a write, see writeKeyFor's header in database-read.ts. */
function notEditableCopy(reason: DbTable['notEditableReason']): string {
  if (reason === 'composite_primary_key') {
    return t(
      "This table can't be edited here yet. Its rows are identified by more than one column.",
    );
  }
  if (reason === 'unsupported_identifier') {
    return t(
      "This table can't be edited here. Some of its names use characters this editor can't work with.",
    );
  }
  return t(
    "This table can't be edited here. Rows can only be changed when the table has a single column that identifies each one.",
  );
}

function compare(a: DbCell, b: DbCell): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return cellText(a).localeCompare(cellText(b));
}

/** A blank row, typed the way its columns are typed. */
function blankRow(table: DbTable, rows: DbRow[], id: string): DbRow {
  const cells: Record<string, DbCell> = {};
  for (const column of table.columns) {
    if (column.primaryKey && column.type === 'number') {
      const highest = rows.reduce(
        (top, row) => Math.max(top, Number(row.cells[column.name]) || 0),
        0,
      );
      cells[column.name] = highest + 1;
    } else if (column.nullable) {
      cells[column.name] = null;
    } else if (column.type === 'number') {
      cells[column.name] = 0;
    } else if (column.type === 'boolean') {
      cells[column.name] = false;
    } else if (column.type === 'timestamp') {
      cells[column.name] = new Date().toISOString().slice(0, 16).replace('T', ' ');
    } else {
      cells[column.name] = '';
    }
  }
  return { id, cells };
}

/* --------------------------------------------------------------- screen */

export default function TableGrid() {
  const slug = useProjectSlug() ?? '';
  const params = useParams();
  const name = params.table ? decodeURIComponent(params.table) : '';
  const navigate = useNavigate();
  const { search } = useLocation();
  // ?state= is a design-review affordance for the mock build only, see
  // use-screen-state.ts's note.
  const forced = USE_MOCK ? new URLSearchParams(search).get('state') : null;

  const [advanced] = useAdvanced();
  const small = useSmallScreen();
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const speed = useDemoStore((s) => s.prefs.speed);

  const { data, isLoading } = useAsync(async () => {
    const project = await getProject(slug);
    if (!project) return null;
    const tables = await listTables(project.id);
    const table = tables.find((one) => one.name === name) ?? null;
    const page = table ? await listRowsPage(project.id, table.name) : null;
    return { project, table, rows: page?.rows ?? [], nextAfter: page?.nextAfter ?? null };
  }, [slug, name, scenarioId]);

  const [rows, setRows] = useState<DbRow[]>([]);
  const [nextAfter, setNextAfter] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [edits, setEdits] = useState<Edits>({});
  const [picked, setPicked] = useState<string[]>([]);
  const [added, setAdded] = useState<string[]>([]);
  const [sort, setSort] = useState<GridSort | null>(null);
  const [filter, setFilter] = useState<Filter | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFilter, setDraftFilter] = useState<Filter | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [zebra, setZebra] = useState(false);

  const table = data?.table ?? null;
  const system = table ? isSystemTable(table.name) : false;
  // The mock build never sets `editable` (every mock table is fully
  // editable); the real build gets it from cloud-api's writeKeyFor, which
  // refuses a table with no single-column primary key or an unsafe
  // identifier. Read-only here, not a disabled button, so nothing on this
  // screen can claim a save that never happened.
  const editable = table ? (table.editable ?? true) : true;
  const readOnly = system || small || !editable;
  const [saveError, setSaveError] = useState<string | null>(null);

  // Every fakeable state is set here, once, from the fixture, so a reviewer
  // opening ?state=diff sees the same three changes a reviewer opening
  // ?state=edits would have made by hand.
  useEffect(() => {
    if (!data || !table) return;

    setRows(forced === 'no-rows' ? [] : data.rows);
    setNextAfter(forced === 'no-rows' ? null : data.nextAfter);
    setPicked([]);
    setAdded([]);
    setSort(null);

    const staged = STAGED_EDITS[table.name] ?? [];
    if ((forced === 'edits' || forced === 'diff') && staged.length > 0) {
      const next: Edits = {};
      for (const one of staged) {
        next[one.rowId] = { ...(next[one.rowId] ?? {}), [one.column]: one.value };
      }
      setEdits(next);
    } else {
      setEdits({});
    }
    setDiffOpen(forced === 'diff' && staged.length > 0);

    if (forced === 'delete') {
      setPicked(data.rows.slice(0, 2).map((row) => row.id));
      setConfirmOpen(true);
    } else {
      setConfirmOpen(false);
    }

    if (forced === 'filter') {
      // The spec's one fixture filter is "minutes < 30". On a table without a
      // minutes column it falls back to the first number column that is not
      // an identifier, so the state opens on every table rather than only on
      // the one it was written for.
      const numeric =
        table.columns.find((one) => one.name === 'minutes') ??
        table.columns.find((one) => one.type === 'number' && !one.primaryKey);
      setFilter(numeric ? { column: numeric.name, op: 'lt', value: '30' } : null);
      setFilterOpen(Boolean(numeric));
    } else {
      setFilter(null);
      setFilterOpen(false);
    }
  }, [data, table, forced]);

  const visible = useMemo(() => {
    let list = filter ? rows.filter((row) => matches(row, filter)) : rows;
    if (sort) {
      const direction = sort.direction === 'asc' ? 1 : -1;
      list = [...list].sort(
        (a, b) => compare(a.cells[sort.column], b.cells[sort.column]) * direction,
      );
    }
    return list;
  }, [rows, filter, sort]);

  useEffect(() => {
    if (data?.project && table) {
      captureEvent('table_grid_viewed', {
        project_slug: data.project.slug,
        table_name: table.name,
        row_count: rows.length,
        column_count: table.columns.length,
      });
    }
  }, [data?.project, table, rows.length]);

  if (isLoading || forced === 'loading') {
    return (
      <div className="page">
        <Skeleton shape="row" count={8} label={t('Loading your rows')} />
      </div>
    );
  }

  if (!data || !table) {
    return (
      <div className="page">
        <EmptyState
          mark="table"
          sentence={t('We cannot find that table.')}
          context={t('Your app may have renamed it, or the address may have a typo in it.')}
          action={
            <Button variant="outline" onClick={() => navigate(`/p/${slug}/database`)}>
              {t('Database')}
            </Button>
          }
        />
      </div>
    );
  }

  const project = data.project;
  const base = `/p/${encodeURIComponent(project.slug)}`;
  const human = humanColumn(table.columns);

  // A join table has no human column at all: every column in it is an
  // identifier. Naming a row "Row 4" there is honest, and dressing an id up
  // in quotation marks as though it were a title is not.
  const named = !human.primaryKey;

  /**
   * A row that exists is named by the value that is still in the database,
   * because that is the name the person is looking for when they scan the
   * sheet. A row being added is named by what they typed, because nothing
   * else about it has ever been true.
   */
  function labelOf(row: DbRow, staged = false): string {
    const held = staged ? edits[row.id]?.[human.name] : undefined;
    const value = held !== undefined ? held : row.cells[human.name];
    const text = cellText(value);
    if (!named) return t('Row {id}', { id: text });
    return text === '' ? t('Empty row') : text;
  }

  /* ---- staging. A row that has never been saved has nothing to diff ---- */

  function onEditsChange(next: Edits) {
    const kept: Edits = {};
    const patch: Record<string, Record<string, DbCell>> = {};
    let touched = false;

    for (const [rowId, columns] of Object.entries(next)) {
      if (added.includes(rowId)) {
        patch[rowId] = columns;
        touched = true;
      } else {
        kept[rowId] = columns;
      }
    }

    if (touched) {
      setRows((was) =>
        was.map((row) =>
          patch[row.id] ? { ...row, cells: { ...row.cells, ...patch[row.id] } } : row,
        ),
      );
    }
    setEdits(kept);
  }

  const changes: CellChange[] = Object.entries(edits).flatMap(([rowId, cols]) => {
    const row = rows.find((one) => one.id === rowId);
    if (!row) return [];
    return Object.entries(cols).map(([column, after]) => ({
      rowId,
      rowLabel: labelOf(row),
      column,
      before: row.cells[column],
      after,
    }));
  });

  const additions: RowAddition[] = added.flatMap((id) => {
    const row = rows.find((one) => one.id === id);
    return row ? [{ rowId: id, rowLabel: labelOf(row, true) }] : [];
  });

  async function save() {
    setSaving(true);
    setSaveError(null);
    const total = changes.length + additions.length;
    captureEvent('table_grid_edits_applied', {
      table_name: table?.name,
      change_count: changes.length,
      addition_count: additions.length,
    });

    if (USE_MOCK) {
      const ms = speed === 'instant' ? 0 : SAVE_MS / speed;
      await new Promise((resolve) => window.setTimeout(resolve, ms));

      setRows((was) =>
        was.map((row) =>
          edits[row.id] ? { ...row, cells: { ...row.cells, ...edits[row.id] } } : row,
        ),
      );
      setEdits({});
      setAdded([]);
      setSaving(false);
      setDiffOpen(false);
      showToast(total === 1 ? t('1 change saved.') : t('{n} changes saved.', { n: total }));
      return;
    }

    if (!table) {
      setSaving(false);
      return;
    }
    const primaryKey = table.columns.find((c) => c.primaryKey)?.name;
    try {
      const saved = new Map<string, DbRow>();
      for (const id of added) {
        const row = rows.find((one) => one.id === id);
        if (!row) continue;
        // The primary key is server-generated on insert, never guessed.
        const cells = { ...row.cells };
        if (primaryKey) delete cells[primaryKey];
        saved.set(id, await insertRow(table.projectId, table.name, cells));
      }
      for (const [rowId, columns] of Object.entries(edits)) {
        if (added.includes(rowId)) continue;
        saved.set(rowId, await updateRow(table.projectId, table.name, rowId, columns));
      }

      setRows((was) =>
        was.map((row) => {
          const result = saved.get(row.id);
          return result ? result : row;
        }),
      );
      setEdits({});
      setAdded([]);
      setDiffOpen(false);
      showToast(total === 1 ? t('1 change saved.') : t('{n} changes saved.', { n: total }));
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : t('Something went wrong. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  function addRow() {
    if (!table) return;
    captureEvent('table_grid_row_added', { table_name: table.name });
    const id = `new-${added.length + 1}-${Date.now()}`;
    setRows((was) => [...was, blankRow(table, was, id)]);
    setAdded((was) => [...was, id]);
  }

  async function deletePicked() {
    const gone = picked.length;
    captureEvent('table_grid_rows_deleted', {
      table_name: table?.name,
      deleted_count: gone,
    });

    if (!USE_MOCK && table) {
      setSaveError(null);
      // A row still in `added` was never saved: nothing to delete server-side.
      const persisted = picked.filter((id) => !added.includes(id));
      try {
        for (const id of persisted) {
          await deleteRow(table.projectId, table.name, id);
        }
      } catch (err) {
        setSaveError(err instanceof ApiError ? err.message : t('Something went wrong. Try again.'));
        return;
      }
    }

    setRows((was) => was.filter((row) => !picked.includes(row.id)));
    setAdded((was) => was.filter((id) => !picked.includes(id)));
    setPicked([]);
    setConfirmOpen(false);
    showToast(gone === 1 ? t('1 row deleted.') : t('{n} rows deleted.', { n: gone }));
  }

  async function showMoreRows() {
    if (!data?.project || !table || nextAfter === null) return;
    setLoadingMore(true);
    try {
      const page = await listRowsPage(data.project.id, table.name, nextAfter);
      setRows((was) => {
        const seen = new Set(was.map((row) => row.id));
        return [...was, ...page.rows.filter((row) => !seen.has(row.id))];
      });
      setNextAfter(page.nextAfter);
    } catch {
      showToast(t("Couldn't load more rows. Try again."));
    } finally {
      setLoadingMore(false);
    }
  }

  /* ---- the footer, which is where the cap is stated as a normal fact ---- */

  // An empty table has no range to state and no cap to explain. The empty
  // state above the footer is already saying the only true thing there is.
  const capped = rows.length > 0 && rows.length < table.rowCount;
  const rangeLabel = filter
    ? visible.length === 1
      ? t('1 row matches this filter')
      : t('{n} rows match this filter', { n: count(visible.length) })
    : rows.length === 0
      ? t('No rows yet')
      : rows.length === 1
        ? t('Row 1 of 1')
        : t('Rows 1 to {last} of {total}', {
            last: count(rows.length),
            total: count(Math.max(rows.length, table.rowCount)),
          });

  const footer = (
    <>
      <span className="grid__range">{rangeLabel}</span>
      {capped && !filter ? (
        <span className="grid__cap">
          {t("We're showing the first {n} rows to keep this fast.", { n: count(rows.length) })}
        </span>
      ) : null}
      {nextAfter !== null && !filter ? (
        <Button variant="ghost" onClick={showMoreRows} loading={loadingMore}>
          {loadingMore ? t('Loading more rows') : t('Show more rows')}
        </Button>
      ) : null}
    </>
  );

  const pickedRows = rows.filter((row) => picked.includes(row.id));
  const names = pickedRows.map((row) => labelOf(row));
  const deleteTitle =
    named && names.length === 1
      ? t('Delete "{name}"?', { name: names[0] })
      : named && names.length === 2
        ? t('Delete "{first}" and "{second}"?', { first: names[0], second: names[1] })
        : names.length === 1
          ? t('Delete 1 row?')
          : t('Delete {n} rows?', { n: count(names.length) });

  const columnOptions = table.columns.map((column) => ({
    value: column.name,
    label: column.name,
  }));

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="tg__crumb">
          <Link className="tg__crumb-link" to={`${base}/database`}>
            {t('Database')}
          </Link>
          <span className="tg__crumb-sep" aria-hidden="true">
            /
          </span>
          <span className="tg__crumb-name">{table.name}</span>
        </h1>
      </header>

      {system ? (
        <Notice tone="quiet" title={t('Managed by SV Cloud')}>
          <p>{t('This table records the changes your app has made to your other tables.')}</p>
        </Notice>
      ) : null}

      {!system && !editable ? (
        <Notice tone="quiet" title={t('Browsing only, for now')}>
          <p>{notEditableCopy(table.notEditableReason)}</p>
        </Notice>
      ) : null}

      {saveError ? (
        <Notice tone="attention" title={t("Your changes weren't saved")}>
          <p>{saveError}</p>
        </Notice>
      ) : null}

      {small && !system ? (
        <p className="tg__note">{t('Editing rows works on a bigger screen.')}</p>
      ) : null}

      <div className="tg__bar">
        <span className="tg__count">
          {table.rowCount === 1 ? t('1 row') : t('{n} rows', { n: count(table.rowCount) })}
        </span>

        <Button
          variant="ghost"
          onClick={() => {
            setDraftFilter(
              filter ?? { column: table.columns[0].name, op: 'contains', value: '' },
            );
            setFilterOpen((was) => !was);
          }}
        >
          {filter ? t('Filter is on') : t('Filter')}
        </Button>

        <Select
          label={t('Sort')}
          value={sort ? `${sort.column}:${sort.direction}` : ''}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === '') {
              setSort(null);
              return;
            }
            const [column, direction] = raw.split(':');
            setSort({ column, direction: direction === 'desc' ? 'desc' : 'asc' });
          }}
          options={[
            { value: '', label: t('The order your app wrote them') },
            ...table.columns.flatMap((column) => [
              { value: `${column.name}:asc`, label: t('{column}, smallest first', { column: column.name }) },
              { value: `${column.name}:desc`, label: t('{column}, largest first', { column: column.name }) },
            ]),
          ]}
        />

        <span className="tg__spacer" />

        {picked.length > 0 && !readOnly ? (
          <Button variant="danger-outline" onClick={() => setConfirmOpen(true)}>
            {picked.length === 1
              ? t('Delete 1 row')
              : t('Delete {n} rows', { n: count(picked.length) })}
          </Button>
        ) : null}

        {!readOnly ? (
          <Button variant="outline" onClick={addRow}>
            {t('Add row')}
          </Button>
        ) : null}

        {/* A shortcut to the query panel that now sits under the grid in both
            modes. It appends to the end of the toolbar and moves nothing, and
            it is a mode check rather than a disclosure because nothing is
            behind it: the thing it jumps to is one click away in Simple. */}
        {advanced ? (
          <Button variant="outline" onClick={() => navigate(`${base}/database/query`)}>
            {t('Run a query')}
          </Button>
        ) : null}
      </div>

      {filterOpen && draftFilter ? (
        <div className="tg__filter">
          <div className="tg__filter-field">
            <Select
              label={t('Column')}
              value={draftFilter.column}
              onChange={(event) =>
                setDraftFilter({ ...draftFilter, column: event.target.value })
              }
              options={columnOptions}
            />
          </div>
          <div className="tg__filter-field">
            <Select
              label={t('Test')}
              value={draftFilter.op}
              onChange={(event) =>
                setDraftFilter({ ...draftFilter, op: event.target.value as FilterOp })
              }
              options={[
                { value: 'contains', label: t('contains') },
                { value: 'is', label: t('is') },
                { value: 'lt', label: t('is less than') },
                { value: 'gt', label: t('is more than') },
              ]}
            />
          </div>
          <div className="tg__filter-field">
            <Field
              label={t('Value')}
              value={draftFilter.value}
              mono
              onChange={(event) => setDraftFilter({ ...draftFilter, value: event.target.value })}
            />
          </div>
          <div className="tg__filter-actions">
            <Button variant="outline" onClick={() => setFilter(draftFilter)}>
              {t('Apply')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setFilter(null);
                setFilterOpen(false);
              }}
            >
              {t('Clear')}
            </Button>
          </div>
        </div>
      ) : null}

      <DataGrid
        label={table.name}
        columns={table.columns}
        rows={visible}
        edits={edits}
        onEditsChange={onEditsChange}
        onDiscard={() => {
          setRows((was) => was.filter((row) => !added.includes(row.id)));
          setAdded([]);
        }}
        onReview={() => setDiffOpen(true)}
        readOnly={readOnly}
        showTypes={advanced}
        zebra={zebra}
        bleed
        selectable={!readOnly}
        selected={picked}
        onSelectedChange={setPicked}
        newRowIds={added}
        sort={sort}
        onSortChange={setSort}
        footer={footer}
        empty={
          /* A filter that matches nothing and a table that holds nothing are
             different facts, and the same sentence for both is a lie in one
             of the two cases. The filtered one says so and offers the only
             thing that changes it, which is not "Add row": adding a row to
             escape a filter is not what anybody meant to do. */
          filter ? (
            <EmptyState
              mark="table"
              sentence={t('No rows match this filter.')}
              context={t('There are still rows in this table.')}
              action={
                <Button variant="outline" onClick={() => setFilter(null)}>
                  {t('Clear the filter')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              mark="table"
              sentence={t("This table doesn't have any rows yet.")}
              context={t('Rows appear here when your app writes to this table.')}
              action={
                readOnly ? undefined : (
                  <Button variant="outline" onClick={addRow}>
                    {t('Add row')}
                  </Button>
                )
              }
            />
          )
        }
      />

      {/* Advanced appends, below the grid, and never above it. The disclosure
          owns both halves: its trigger in Simple, its ADVANCED chip when the
          account switch is on. */}
      <div className="tg__advanced">
        <AdvancedDisclosure label={t('Show the schema and run a query')}>
          <div className="tg__toggle">
            <Checkbox
              label={t('Shade every other row')}
              help={t('Off by default, because a shaded row can read as a selected one.')}
              checked={zebra}
              onChange={(event) => setZebra(event.target.checked)}
            />
          </div>

          <SectionPanel title={t('Schema')} level={2}>
            {/* The qualified name, because Advanced prints the real one and
                because it is the string somebody pastes into a query. */}
            <div className="tg__schema-key">
              <CopyField label={t('Table')} value={`public.${table.name}`} size="inline" />
            </div>
            <Table<DbColumn>
              caption={t('The columns in this table')}
              rows={table.columns}
              rowKey={(column) => column.name}
              columns={[
                { key: 'name', header: t('Column'), mono: true, render: (column) => column.name },
                { key: 'type', header: t('Type'), mono: true, render: (column) => column.type },
                {
                  key: 'nullable',
                  header: t('Can be empty'),
                  render: (column) => (column.nullable ? t('Yes') : t('No')),
                },
                {
                  key: 'key',
                  header: t('Key'),
                  render: (column) => (column.primaryKey ? t('Primary key') : ''),
                },
              ]}
            />
          </SectionPanel>

          {USE_MOCK ? <QueryPanel slug={project.slug} ink={false} /> : null}
        </AdvancedDisclosure>
      </div>

      <DiffSheet
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        onSave={() => void save()}
        table={table.name}
        changes={changes}
        additions={additions}
        saving={saving}
      />

      <DestructiveConfirm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={deletePicked}
        title={deleteTitle}
        objectName={String(picked.length)}
        confirmLabel={
          picked.length === 1
            ? t('Delete 1 row')
            : t('Delete {n} rows', { n: count(picked.length) })
        }
        typeToConfirm={picked.length > TYPE_TO_CONFIRM_ABOVE}
        typePrompt={t('Type the number of rows to confirm')}
        consequences={[
          {
            kind: 'loss',
            text:
              picked.length === 1
                ? t("This row is deleted from {table}. This can't be undone.", {
                    table: table.name,
                  })
                : t("These rows are deleted from {table}. This can't be undone.", {
                    table: table.name,
                  }),
          },
          { kind: 'survives', text: t('Every other row in this table stays as it is.') },
          { kind: 'survives', text: t('Your app keeps running the whole time.') },
        ]}
      />
    </div>
  );
}
